import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import BackendService from 'BackendService';
import { Permissions } from 'Permission';
import { usePermissions } from 'PermissionContext';
import React, { Suspense } from 'react';
import { DocumentCategory } from '../../models/DocumentCategory';
import { StyledButton } from '../styles/StyledButtons';
import AIModelsSection from './GlobalConfigAdmin/AIModelsSection';
import ConfigTabLabel from './GlobalConfigAdmin/ConfigTabLabel';
import FeatureSwitchesSection from './GlobalConfigAdmin/FeatureSwitchesSection';
import GlobalConfigAdminHeader from './GlobalConfigAdmin/GlobalConfigAdminHeader';
import InvestmentConfigSection from './GlobalConfigAdmin/InvestmentConfigSection';
import MediaContentSection from './GlobalConfigAdmin/MediaContentSection';
import MobileAppSection from './GlobalConfigAdmin/MobileAppSection';
import NotificationsSection from './GlobalConfigAdmin/NotificationsSection';
import UnsavedChangesPanel from './GlobalConfigAdmin/UnsavedChangesPanel';
import VeraExcludedUrlsSection from './GlobalConfigAdmin/VeraExcludedUrlsSection';
import VeraPromptProposalsSection from './GlobalConfigAdmin/VeraPromptProposalsSection';
import VeraResponsesSection from './GlobalConfigAdmin/VeraResponsesSection';
import {
  ADVANCED_MODEL_FIELDS,
  ALL_VERA_FIELDS,
  DOCUMENT_CATEGORY_FIELDS,
  FEATURE_SWITCH_FIELDS,
  INVESTMENT_CONFIG_FIELDS,
  MEDIA_CONTENT_FIELDS,
  MOBILE_APP_FIELDS,
  NOTIFICATION_CONFIG_FIELDS,
  VERA_EXCLUDED_URL_FIELDS,
  VERA_PROMPT_PROPOSAL_FIELDS,
  VERA_RESPONSES_FIELDS,
} from './GlobalConfigAdmin/globalConfigFieldConfig';
import { useGlobalConfigAdmin } from './GlobalConfigAdmin/useGlobalConfigAdmin';

const DocumentCategoriesSection = React.lazy(
  () => import('./GlobalConfigAdmin/DocumentCategoriesSection'),
);

export default function GlobalConfigAdmin() {
  const { hasPermission } = usePermissions();
  const canManageDocumentCategories =
    hasPermission(Permissions.DocumentCategoriesManage) ||
    hasPermission(Permissions.DocumentCategoriesAdd);

  const [documentCategories, setDocumentCategories] = React.useState<DocumentCategory[]>([]);
  React.useEffect(() => {
    if (!canManageDocumentCategories) return;
    new BackendService()
      .listDocumentCategories()
      .then(setDocumentCategories)
      .catch(() => setDocumentCategories([]));
  }, [canManageDocumentCategories]);
  const categoryNameById = React.useMemo(
    () => Object.fromEntries(documentCategories.map((c) => [c.id, c.name])),
    [documentCategories],
  );

  const {
    config,
    originalConfig,
    loading,
    saving,
    tab,
    setTab,
    generalTab,
    setGeneralTab,
    veraTab,
    setVeraTab,
    advancedTab,
    setAdvancedTab,
    dirtyFields,
    dropdownSx,
    handleChange,
    setAllPushTypes,
    revertFieldChange,
    resolveDropdownOptions,
    save,
    isDirty,
  } = useGlobalConfigAdmin();

  if (loading || !config || !originalConfig) return <CircularProgress />;

  return (
    <>
      <GlobalConfigAdminHeader />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab
          value="general"
          label={
            <ConfigTabLabel
              label="General"
              dirty={isDirty([
                ...FEATURE_SWITCH_FIELDS,
                ...INVESTMENT_CONFIG_FIELDS,
                ...NOTIFICATION_CONFIG_FIELDS,
                ...MOBILE_APP_FIELDS,
                ...DOCUMENT_CATEGORY_FIELDS,
              ])}
            />
          }
        />
        <Tab
          value="vera"
          label={<ConfigTabLabel label="Vera" dirty={isDirty(ALL_VERA_FIELDS)} />}
        />
        <Tab
          value="advanced"
          label={<ConfigTabLabel label="Advanced" dirty={isDirty(ADVANCED_MODEL_FIELDS)} />}
        />
      </Tabs>

      {tab === 'general' && (
        <>
          <Tabs value={generalTab} onChange={(_, v) => setGeneralTab(v)} sx={{ mb: 2 }}>
            <Tab
              value="feature"
              label={
                <ConfigTabLabel label="Feature Switches" dirty={isDirty(FEATURE_SWITCH_FIELDS)} />
              }
            />
            <Tab
              value="investment"
              label={
                <ConfigTabLabel
                  label="Investment Configuration"
                  dirty={isDirty(INVESTMENT_CONFIG_FIELDS)}
                />
              }
            />
            <Tab
              value="notifications"
              label={
                <ConfigTabLabel label="Notifications" dirty={isDirty(NOTIFICATION_CONFIG_FIELDS)} />
              }
            />
            <Tab
              value="mobile-app"
              label={<ConfigTabLabel label="Mobile App" dirty={isDirty(MOBILE_APP_FIELDS)} />}
            />
            {canManageDocumentCategories && (
              <Tab
                value="document-categories"
                label={
                  <ConfigTabLabel
                    label="Document Categories"
                    dirty={isDirty(DOCUMENT_CATEGORY_FIELDS)}
                  />
                }
              />
            )}
          </Tabs>

          {generalTab === 'feature' && (
            <FeatureSwitchesSection
              config={config}
              dirtyFields={dirtyFields}
              onChange={handleChange}
            />
          )}

          {generalTab === 'investment' && (
            <InvestmentConfigSection
              config={config}
              dirtyFields={dirtyFields}
              onChange={handleChange}
            />
          )}

          {generalTab === 'notifications' && (
            <NotificationsSection
              config={config}
              dirtyFields={dirtyFields}
              onChange={handleChange}
              onSetAllPushTypes={setAllPushTypes}
            />
          )}

          {generalTab === 'mobile-app' && (
            <MobileAppSection config={config} dirtyFields={dirtyFields} onChange={handleChange} />
          )}

          {generalTab === 'document-categories' && canManageDocumentCategories && (
            <Suspense fallback={<CircularProgress />}>
              <DocumentCategoriesSection
                config={config}
                categories={documentCategories}
                dirtyFields={dirtyFields}
                onChange={handleChange}
              />
            </Suspense>
          )}
        </>
      )}

      {tab === 'vera' && (
        <>
          <Tabs value={veraTab} onChange={(_, v) => setVeraTab(v)} sx={{ mb: 2 }}>
            <Tab
              value="proposals"
              label={
                <ConfigTabLabel
                  label="Prompt Proposals"
                  dirty={isDirty(VERA_PROMPT_PROPOSAL_FIELDS)}
                />
              }
            />
            <Tab
              value="responses"
              label={
                <ConfigTabLabel
                  label="Responses & Guardrails"
                  dirty={isDirty(VERA_RESPONSES_FIELDS)}
                />
              }
            />
            <Tab
              value="excluded-urls"
              label={
                <ConfigTabLabel label="Excluded URLs" dirty={isDirty(VERA_EXCLUDED_URL_FIELDS)} />
              }
            />
          </Tabs>

          {veraTab === 'proposals' && (
            <VeraPromptProposalsSection
              config={config}
              dirtyFields={dirtyFields}
              dropdownSx={dropdownSx}
              onChange={handleChange}
            />
          )}

          {veraTab === 'responses' && (
            <VeraResponsesSection
              config={config}
              dirtyFields={dirtyFields}
              onChange={handleChange}
            />
          )}

          {veraTab === 'excluded-urls' && (
            <VeraExcludedUrlsSection
              config={config}
              dirtyFields={dirtyFields}
              onChange={handleChange}
            />
          )}
        </>
      )}

      {tab === 'advanced' && (
        <>
          <Tabs value={advancedTab} onChange={(_, v) => setAdvancedTab(v)} sx={{ mb: 2 }}>
            <Tab
              value="ai-models"
              label={<ConfigTabLabel label="AI Models" dirty={isDirty(ADVANCED_MODEL_FIELDS)} />}
            />
            <Tab
              value="media-content"
              label={
                <ConfigTabLabel label="Media and Content" dirty={isDirty(MEDIA_CONTENT_FIELDS)} />
              }
            />
          </Tabs>

          {advancedTab === 'ai-models' && (
            <AIModelsSection
              config={config}
              dirtyFields={dirtyFields}
              dropdownSx={dropdownSx}
              onChange={handleChange}
              getDropdownOptions={resolveDropdownOptions}
            />
          )}

          {advancedTab === 'media-content' && (
            <MediaContentSection
              config={config}
              dirtyFields={dirtyFields}
              onChange={handleChange}
            />
          )}
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 4 }}>
        <StyledButton
          onClick={save}
          disabled={saving || dirtyFields.size === 0}
          sx={{ alignSelf: 'flex-start' }}
        >
          Save
        </StyledButton>

        <UnsavedChangesPanel
          dirtyFields={dirtyFields}
          config={config}
          originalConfig={originalConfig}
          categoryNameById={categoryNameById}
          onRevertField={revertFieldChange}
        />
      </Box>
    </>
  );
}
